import Close from '@mui/icons-material/Close';
import ExpandCircleDownOutlined from '@mui/icons-material/ExpandCircleDownOutlined';
import Box from '@mui/material/Box';
import Drawer, { drawerClasses } from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {
  AccountType,
  type ModelError_Item,
  type ModelError_Response,
  UnitOfDistance,
  WaypointType,
} from '@treadinc/horizon-api-spec';
import type { AxiosError } from 'axios';
import { t } from 'i18next';
import { uniqBy } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  LoadingSpinner,
  SmallButton,
} from '~components/Order/ordersDispatchStyledComponents';
import PricingHistoryButton from '~components/PricingHistory/PricingHistoryButton';
import { FeatureFlags } from '~constants/featureFlags';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import { FormStateChangeProps } from '~formsShared/types';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import { deparseCreateV3, deparseUpdateV3 } from '~hooks/useOrders/deparseV3';
import { Order } from '~hooks/useOrders/models';
import useOrdersDispatch from '~hooks/useOrders/useOrdersDispatch';
import { useServices } from '~hooks/useServices/useServices';
import { decoupleOrderFromProjectRates } from '~hooks/useSiteRateSchedule/decoupleOrderFromProjectRates';
import { SiteRateScheduleSaveResult } from '~hooks/useSiteRateSchedule/siteRateScheduleSaveResult';
import useSiteRateScheduleOperations from '~hooks/useSiteRateSchedule/useSiteRateScheduleOperations';
import { Site } from '~hooks/useSites/models';
import { type DefaultSitesWaypointType, useSites } from '~hooks/useSites/useSites';
import { createSitesInline } from '~pages/Sales/Orders/helpers';
import { NewOrderFormSchemaInterface } from '~pages/Sales/Orders/newOrderFormSchema';
import type { OrderFormHandle } from '~pages/Sales/Orders/orderFormHandle';
import {
  OrderFormV3,
  type OrderFormV3Handle,
  type OrderFormV3SubmitPayload,
} from '~pages/Sales/Orders/orderFormV3/OrderFormV3';
import { OrderFormV3Provider } from '~pages/Sales/Orders/orderFormV3/OrderFormV3Context';
import type { SiteRateScheduleOperations } from '~pages/Sales/Projects/components/SiteRatesForm';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { useAwaitableFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  hasDuplicateAddOnAssignmentFailure,
  processAddOnAssignmentsForOrder,
} from '../utils/processAddOnAssignmentsForOrder';
import { resolveDefaultSiteRateScheduleId } from '../utils/resolveDefaultSiteRateScheduleId';
import useOrderCreatedToaster from './useOrderCreatedToaster';

type AddDefaultSiteParams = {
  siteId: string;
  waypointType: DefaultSitesWaypointType;
};

type NewDefaultSitesState = {
  pickUp: AddDefaultSiteParams[];
  dropOff: AddDefaultSiteParams[];
};

type NewOrderFormStateChange = FormStateChangeProps & Partial<AddDefaultSiteParams>;

/**
 * Name the just-saved order as the cascade origin when the form could not.
 *
 * A rate edit made while CREATING an order (or copying one) is a write to the project's rate card, so
 * it cascades. The backend spares exactly one order from that cascade — the origin — and computes its
 * skip set as "everything reachable minus the origin". A brand-new order is reachable the moment it
 * exists (the audience is decided by order state alone, and a new order is `created`), and the card
 * write runs AFTER the order POST. So with no origin the subtraction is a no-op and the edit reaches
 * NO order at all, including the one the user just made it on.
 *
 * The form cannot supply the id — the order does not exist while it is assembling the operations — so
 * it is stamped here, at the first point the saved order is known. An origin the form already knew
 * (editing an existing order) is never overwritten. Only relevant when the checkbox surfaced and set
 * the override; a save that never touched the cascade controls is left alone.
 */
const stampOriginOrderId = (
  operations: SiteRateScheduleOperations,
  savedOrder: Order,
): void => {
  if (
    operations.applyRateChangesToExistingOrders !== undefined &&
    !operations.originOrderId
  ) {
    operations.originOrderId = savedOrder.id;
  }
};

/**
 * Restates a save result's rate ids as the ids the rate manager's own rows carry.
 *
 * A decoupled save writes with the order's copy ids while the rows keep the project ids they were
 * built from. The rate manager reconciles its rows and its next dirty-check snapshot by the id it
 * was written with, so a copy id strands that snapshot under a key the next dirty check never
 * reads — leaving every row that just saved permanently dirty, and its retry re-emitting writes
 * that already landed. Created cards keep their new ids: those records really are the order's own.
 */
const withSourceRateIds = (
  result: SiteRateScheduleSaveResult,
  clonedSiteRateSchedules: Record<string, string>,
): SiteRateScheduleSaveResult => {
  const sourceOfCopy = new Map(
    Object.entries(clonedSiteRateSchedules).map(([sourceId, copyId]) => [
      copyId,
      sourceId,
    ]),
  );
  if (sourceOfCopy.size === 0) return result;

  return {
    creates: result.creates,
    updates: result.updates.map((op) => ({
      ...op,
      rateId: sourceOfCopy.get(op.rateId) ?? op.rateId,
    })),
    deletes: result.deletes.map((op) => ({
      ...op,
      rateId: sourceOfCopy.get(op.rateId) ?? op.rateId,
    })),
  };
};

interface OrderFormProps {
  defaultDate?: string;
  disableDispatchDeepLink?: boolean;
  isCopyAndEdit?: boolean;
  isMinimized?: boolean;
  isOpen: boolean;
  keepMounted?: boolean;
  onClose: () => void;
  onMinimize?: () => void;
  onOrderSaved?: (order: Order, action: 'created' | 'updated') => void;
  order?: Order;
}

const OrderForm = observer(
  ({
    defaultDate,
    disableDispatchDeepLink,
    isCopyAndEdit,
    isMinimized,
    isOpen,
    keepMounted,
    onClose,
    onMinimize,
    onOrderSaved,
    order,
  }: OrderFormProps) => {
    const theme = useTheme();
    const isEditing = Boolean(order?.id) && !isCopyAndEdit;

    const formRef = useRef<OrderFormV3Handle>(null);

    const [drawerState, setDrawerState] = useState<{
      isOpen: boolean;
      triggerCallbackOnTransitionExited?: 'onMinimize' | 'onClose';
    }>({ isOpen: isOpen && !isMinimized, triggerCallbackOnTransitionExited: undefined });

    useEffect(() => {
      setDrawerState({ isOpen: isOpen && !isMinimized });
      if (isMinimized) {
        formRef.current?.pauseSectionTiming();
      }
    }, [isOpen, isMinimized]);

    const [formIsDirty, setFormIsDirty] = useState(false);
    const [newDefaultSites, setNewDefaultSites] = useState<NewDefaultSitesState>({
      pickUp: [],
      dropOff: [],
    });
    const { addSiteToOrderDefaultSites, createNewSite } = useSites();
    const { userStore, ordersDispatchStore, companyAssetsStore, toasterStore } =
      useStores();
    const { createOrderV3, updateOrderV3 } = useOrdersDispatch();
    const { processSiteRateScheduleOperationsResilient } =
      useSiteRateScheduleOperations();
    // When a previous submit landed the order but some Site Rate Schedules failed, the
    // dialog needs to remember the saved order so the retry submit PATCHes the existing
    // record instead of POSTing a duplicate. Mirrors `createdProject` in ProjectDialog.
    const [partialFailureOrder, setPartialFailureOrder] = useState<Order | null>(null);
    // Footer status row mirroring ProjectDialog's mock — a short summary of the most
    // recent submit (order saved / failed + rate counts) shown next to the submit button
    // instead of a transient toast.
    const [lastSubmitSummary, setLastSubmitSummary] = useState<{
      orderStatus: 'saved' | 'failed';
      ratesAttempted: boolean;
      ratesSucceededCount: number;
      ratesFailedCount: number;
      addOnsFailedCount: number;
      addOnsDuplicate?: boolean;
    } | null>(null);
    // Rate records the order owns because this drawer session put them there — the copies a
    // decoupling minted plus everything the order-scoped writes created. A retry re-emits rows
    // carrying these ids, and the decoupling's redirect has to recognise them as already the
    // order's rather than refuse to write them.
    const orderOwnedRateIdsRef = useRef<Set<string>>(new Set());
    // A save 422's ModelError_Item[] body, flowed into the Provider so the
    // inline-error mapping can surface each item under its field.
    const [asyncErrors, setAsyncErrors] = useState<ModelError_Item[]>([]);
    const companyId = getEffectiveUserCompanyId(userStore);

    const services = companyAssetsStore.services;
    const { getAllServices } = useServices();

    const dispatchSmartAutoRefreshFeatureFlag = useAwaitableFeatureFlag({
      featureFlagKey: FeatureFlags.dispatchSmartAutoRefresh,
    });
    const shouldAutoOpenCreatedOrder =
      dispatchSmartAutoRefreshFeatureFlag.isReady &&
      dispatchSmartAutoRefreshFeatureFlag.isFeatureFlagEnabled;

    const wasOpenRef = useRef(false);
    useEffect(() => {
      if (isOpen && !wasOpenRef.current) {
        AnalyticsController.track('order_form_v3_opened', { is_editing: isEditing });
        formRef.current?.resetSectionTiming();
      }
      wasOpenRef.current = isOpen;
    }, [isOpen, isEditing]);

    const { showOrderCreatedToaster } = useOrderCreatedToaster();

    const [isLoading, setIsLoading] = useState({
      services: true,
    });

    const isLoadingInitialData = Object.values(isLoading).some((loading) => loading);

    const onOrderCreated = useCallback(
      async (orderId: string, orderCompanyId?: string) => {
        const sites = [...newDefaultSites.pickUp, ...newDefaultSites.dropOff];

        if (sites.length > 0) {
          await sites.reduce(
            async (acc, site) => {
              await acc;
              return await addSiteToOrderDefaultSites({
                orderId,
                siteId: site.siteId,
                waypointType: site.waypointType,
                companyId: orderCompanyId,
              });
            },
            Promise.resolve({} as Site),
          );
        }

        formRef.current?.fileAttachmentsOnSubmit(orderId);
      },
      [newDefaultSites.pickUp, newDefaultSites.dropOff],
    );

    const handleClose = useCallback(
      (
        _event: React.MouseEvent<HTMLButtonElement>,
        reason?: 'backdropClick' | 'escapeKeyDown',
      ) => {
        if (reason === 'backdropClick' && onMinimize) {
          setDrawerState({
            isOpen: false,
            triggerCallbackOnTransitionExited: 'onMinimize',
          });
        } else {
          setDrawerState({
            isOpen: false,
            triggerCallbackOnTransitionExited: 'onClose',
          });
        }
      },
      [onMinimize],
    );

    const handleTransitionExited = useCallback(() => {
      if (drawerState.triggerCallbackOnTransitionExited === 'onMinimize') {
        onMinimize?.();
      } else if (drawerState.triggerCallbackOnTransitionExited === 'onClose') {
        setNewDefaultSites({ pickUp: [], dropOff: [] });
        // Forget any partial-failure-create order so the next time the
        // drawer opens fresh it goes back to POSTing a new record.
        setPartialFailureOrder(null);
        setLastSubmitSummary(null);
        setAsyncErrors([]);
        orderOwnedRateIdsRef.current = new Set();
        onClose();
      }
    }, [drawerState.triggerCallbackOnTransitionExited, onMinimize, onClose]);

    const handleFormStateChange = useCallback(
      ({ isDirty, siteId, waypointType }: NewOrderFormStateChange) => {
        setFormIsDirty(isDirty);

        if (siteId) {
          if (waypointType === WaypointType.PICKUP) {
            setNewDefaultSites((sites) => ({
              ...sites,
              pickUp: uniqBy([...sites.pickUp, { siteId, waypointType }], 'siteId'),
            }));
          }

          if (waypointType === WaypointType.DROP_OFF) {
            setNewDefaultSites((sites) => ({
              ...sites,
              dropOff: uniqBy([...sites.dropOff, { siteId, waypointType }], 'siteId'),
            }));
          }
        }
      },
      [],
    );

    // A default site added in the create flow is only staged here until the
    // order exists. Undoing that addition in the selector must drop the staged
    // entry so `onOrderCreated` never POSTs it.
    const handleTempDefaultSiteRemoved = useCallback(
      ({ siteId, waypointType }: AddDefaultSiteParams) => {
        if (waypointType === WaypointType.PICKUP) {
          setNewDefaultSites((sites) => ({
            ...sites,
            pickUp: sites.pickUp.filter((site) => site.siteId !== siteId),
          }));
        }

        if (waypointType === WaypointType.DROP_OFF) {
          setNewDefaultSites((sites) => ({
            ...sites,
            dropOff: sites.dropOff.filter((site) => site.siteId !== siteId),
          }));
        }
      },
      [],
    );

    const saveOrderV3 = useCallback(
      async (formData: OrderFormV3SubmitPayload) => {
        // Field-level 422s from the previous attempt are stale the moment a
        // new save starts (ProjectDialog parity).
        setAsyncErrors([]);

        AnalyticsController.track('order_form_v3_submit_started', {
          is_editing: isEditing,
        });

        // createSitesInline reads and rewrites only the waypoint site slots
        // (+ company legal name) and returns the same object. The v3 payload
        // carries those keys with narrower runtime types than the legacy
        // schema interface its signature names, so the call crosses the
        // helper's boundary under a documented cast and the result is
        // re-typed to the payload shape it mutated.
        const newFormData = (await createSitesInline(
          formData as unknown as NewOrderFormSchemaInterface,
          createNewSite,
          userStore.userCompany?.siteRadius,
        )) as unknown as
          | OrderFormV3SubmitPayload
          | {
              error: boolean;
              field: Parameters<OrderFormHandle['setFieldError']>[0];
              message?: string;
            };

        if ('error' in newFormData) {
          if (newFormData.message) {
            formRef.current?.setFieldError(newFormData.field, newFormData.message);
          } else {
            const isPickupSiteError = newFormData.field === 'pickUpWayPoint';
            const message = isPickupSiteError
              ? t('error_messages.orders.failed_to_create_pickup_site')
              : t('error_messages.orders.failed_to_create_dropoff_site');

            toasterStore.push(alert(message, AlertTypes.error));
          }

          AnalyticsController.track('order_form_v3_submit_failed', {
            is_editing: isEditing,
          });
          return;
        }

        // Where the project's rate records landed on the order, once this save decoupled it.
        // The starred-card PATCHes below have to follow the star onto its copy, or they
        // re-attach the order to a project card and undo the decoupling.
        let clonedSiteRateSchedules: Record<string, string> = {};
        // The backend only repoints the star while it is minting copies, so a save that decoupled
        // an ALREADY-decoupled order (a retry) leaves the star on the project card this save just
        // sent. Write the copy explicitly when the reported star isn't it.
        let starNeedsRepointAfterDecouple = false;

        // Resilient SRS save — same contract as the legacy commitPendingSrsOps
        // above (the form picks the owner; in-progress orders with their own
        // stamped SRS report target='order'), plus the v3-only decoupling the
        // unchecked apply-rate-changes box asks for.
        const commitPendingSrsOps = async (
          savedOrder: Order,
        ): Promise<
          | { status: 'saved'; result: SiteRateScheduleSaveResult | null }
          | { status: 'aborted'; rateOpCount: number }
        > => {
          const result = formRef.current?.takePendingSiteRateScheduleOperations();
          if (!result) return { status: 'saved', result: null };
          const { operations, target, dereferenceFromProject } = result;
          const projectId = newFormData.project?.id;
          if (target === 'project' && projectId) {
            stampOriginOrderId(operations, savedOrder);

            return {
              status: 'saved',
              result: await processSiteRateScheduleOperationsResilient(
                { type: 'project', projectId },
                operations,
                'order',
              ),
            };
          }
          let orderOperations = operations;
          if (dereferenceFromProject) {
            // Runs here, not in the form's submit assembly, because the order and its star
            // must already be persisted — and before the writes below, which the backend
            // would otherwise read as an order that owns cards and skip the copies for.
            const decoupled = await decoupleOrderFromProjectRates({
              companyId,
              operations,
              orderId: savedOrder.id,
              orderOwnedIds: orderOwnedRateIdsRef.current,
            });
            if (!decoupled) {
              return {
                status: 'aborted',
                rateOpCount:
                  operations.toCreate.length +
                  operations.toUpdate.length +
                  operations.toDelete.length,
              };
            }
            clonedSiteRateSchedules = decoupled.clonedSiteRateSchedules;
            orderOperations = decoupled.operations;
            for (const id of decoupled.orderOwnedIds) {
              orderOwnedRateIdsRef.current.add(id);
            }

            const starredCopyId = newFormData.defaultSiteRateScheduleId
              ? clonedSiteRateSchedules[newFormData.defaultSiteRateScheduleId]
              : undefined;
            starNeedsRepointAfterDecouple =
              Boolean(starredCopyId) &&
              decoupled.defaultSiteRateScheduleId !== starredCopyId;
          }

          const written = await processSiteRateScheduleOperationsResilient(
            { type: 'order', companyId, orderId: savedOrder.id },
            orderOperations,
            'order',
          );
          for (const create of written.creates) {
            if (create.serverId) orderOwnedRateIdsRef.current.add(create.serverId);
          }
          for (const op of [...written.creates, ...written.updates]) {
            for (const addOn of op.addOns) {
              if (addOn.createdAssignmentId) {
                orderOwnedRateIdsRef.current.add(addOn.createdAssignmentId);
              }
            }
          }

          return {
            status: 'saved',
            // The rate manager reconciles its rows and its dirty-check snapshot by the id it was
            // written with, and its rows still hold the project's ids — so a copy id here would
            // key the snapshot where the next dirty check never looks, leaving every row that
            // just saved permanently dirty.
            result: withSourceRateIds(written, clonedSiteRateSchedules),
          };
        };

        // No card write was issued, so the rates are all still pending while the order itself
        // landed — keep it for the retry PATCH and leave the drawer open.
        const reportSrsCommitAborted = (savedOrder: Order, rateOpCount: number) => {
          setPartialFailureOrder(savedOrder);
          setLastSubmitSummary({
            orderStatus: 'saved',
            // A save that only unchecked the box has no rate count to report, and claiming
            // "0 rates saved" in green would read as success for a failed decoupling.
            ratesAttempted: rateOpCount > 0,
            ratesSucceededCount: 0,
            ratesFailedCount: rateOpCount,
            addOnsFailedCount: 0,
          });
          AnalyticsController.track('order_form_v3_submit_partial', {
            is_editing: isEditing,
            srs_failed_count: rateOpCount,
            add_ons_failed_count: 0,
          });
        };

        const handleSrsSaveResult = (
          result: SiteRateScheduleSaveResult | null,
        ): { hasFailures: boolean; succeededCount: number; failedCount: number } => {
          const empty = {
            hasFailures: false,
            succeededCount: 0,
            failedCount: 0,
          };
          if (!result) return empty;
          return formRef.current?.applySiteRateSaveResult(result) ?? empty;
        };

        const starredSiteRateScheduleIdAfterSave = (starredId: string | null) =>
          (starredId && clonedSiteRateSchedules[starredId]) || starredId;

        // Same retry semantics as legacy: a partial-failure submit landed the
        // order, so the retry PATCHes it instead of POSTing a duplicate.
        const treatAsUpdate = isEditing || partialFailureOrder !== null;
        // Same unit source the legacy hooks derive internally — the
        // deparsers' meter conversions depend on it.
        const distanceUnit = userStore.userCompany?.isMeters
          ? UnitOfDistance.KILOMETER
          : UnitOfDistance.MILE;

        try {
          if (treatAsUpdate) {
            // Copy & Edit partial-failure retry: defer a still-clientKey
            // starred id off this PATCH and resolve it after the SRS save —
            // same reasoning as the legacy branch above.
            const retryNeedsSrsDeferral =
              isCopyAndEdit &&
              partialFailureOrder !== null &&
              newFormData.defaultSiteRateScheduleId !== null;
            const sourceDefaultSrsId = retryNeedsSrsDeferral
              ? newFormData.defaultSiteRateScheduleId
              : null;
            const baseDataForUpdate = partialFailureOrder
              ? { ...newFormData, id: partialFailureOrder.id }
              : newFormData;
            const dataForUpdate = retryNeedsSrsDeferral
              ? { ...baseDataForUpdate, defaultSiteRateScheduleId: null }
              : baseDataForUpdate;

            // Sparse PATCH: only dirtyFields-gated keys reach the
            // body. An EMPTY body means the user submitted without any
            // parent-level change — skip the API call and reuse the saved
            // order so the SRS/add-on stages below still process their own
            // state (the ProjectDialog empty-PATCH precedent).
            const dirtyFields = formRef.current?.getDirtyFields?.() ?? {};
            const updateBody = deparseUpdateV3(dataForUpdate, dirtyFields, distanceUnit);

            let updatedOrder: Order;
            if (Object.keys(updateBody).length === 0) {
              // treatAsUpdate=true requires `isEditing || partialFailureOrder
              // !== null`, so one of `partialFailureOrder` / `order` is always
              // defined. The fallback throw keeps updatedOrder narrowed
              // without a non-null assertion.
              const existing = partialFailureOrder ?? order ?? null;
              if (!existing) {
                throw new Error('v3 empty-PATCH skip: no order to reuse');
              }
              updatedOrder = existing;
            } else {
              updatedOrder = await updateOrderV3({
                id: dataForUpdate.id ?? '',
                body: updateBody,
              });
            }

            const srsCommit = await commitPendingSrsOps(updatedOrder);
            if (srsCommit.status === 'aborted') {
              reportSrsCommitAborted(updatedOrder, srsCommit.rateOpCount);

              return;
            }
            const srsResult = srsCommit.result;
            const srsSummary = handleSrsSaveResult(srsResult);
            if (srsSummary.hasFailures) {
              setPartialFailureOrder(updatedOrder);
              setLastSubmitSummary({
                orderStatus: 'saved',
                ratesAttempted: true,
                ratesSucceededCount: srsSummary.succeededCount,
                ratesFailedCount: srsSummary.failedCount,
                addOnsFailedCount: 0,
              });
              AnalyticsController.track('order_form_v3_submit_partial', {
                is_editing: isEditing,
                srs_failed_count: srsSummary.failedCount,
                add_ons_failed_count: 0,
              });
              return;
            }

            const starredIdAfterCommit =
              retryNeedsSrsDeferral || starNeedsRepointAfterDecouple
                ? starredSiteRateScheduleIdAfterSave(
                    resolveDefaultSiteRateScheduleId(
                      sourceDefaultSrsId ?? newFormData.defaultSiteRateScheduleId,
                      srsResult,
                    ),
                  )
                : null;
            if (starredIdAfterCommit) {
              try {
                // Single-field sparse PATCH: a one-key dirty map gates
                // everything else out; the deparser map supplies the BE key
                // (legacy re-sent the full body to set the same one field).
                await updateOrderV3({
                  id: updatedOrder.id,
                  body: deparseUpdateV3(
                    {
                      ...newFormData,
                      defaultSiteRateScheduleId: starredIdAfterCommit,
                    },
                    { defaultSiteRateScheduleId: true },
                    distanceUnit,
                  ),
                });
              } catch {
                // updateOrderV3 already toasts non-422 failures. Fall through
                // so the user sees the order was updated; the starred
                // reference is the only thing missing and can be re-set
                // manually.
              }
            }

            formRef.current?.updateProjectDefaultSites();
            formRef.current?.fileAttachmentsOnSubmit(updatedOrder.orderId);

            const {
              vendorAddOnAssignments,
              customerAddOnAssignments,
              driverAddOnAssignments,
            } = formData;

            const updateBatches = [
              { items: vendorAddOnAssignments ?? [], accountType: AccountType.VENDOR },
              {
                items: customerAddOnAssignments ?? [],
                accountType: AccountType.CUSTOMER,
              },
              { items: driverAddOnAssignments ?? [], accountType: undefined },
            ];
            const updateAddOnResults = await Promise.allSettled(
              updateBatches.map((b) =>
                b.items.length > 0
                  ? processAddOnAssignmentsForOrder({
                      companyId,
                      order: updatedOrder,
                      addOnAssignments: b.items,
                      accountType: b.accountType,
                    })
                  : Promise.resolve([]),
              ),
            );
            const updateAddOnsFailedCount = updateAddOnResults.reduce(
              (sum, r, i) =>
                r.status === 'rejected' ? sum + updateBatches[i].items.length : sum,
              0,
            );
            if (updateAddOnsFailedCount > 0) {
              setPartialFailureOrder(updatedOrder);
              setLastSubmitSummary({
                orderStatus: 'saved',
                ratesAttempted: srsResult !== null,
                ratesSucceededCount: srsSummary.succeededCount,
                ratesFailedCount: 0,
                addOnsFailedCount: updateAddOnsFailedCount,
                addOnsDuplicate: hasDuplicateAddOnAssignmentFailure(updateAddOnResults),
              });
              AnalyticsController.track('order_form_v3_submit_partial', {
                is_editing: isEditing,
                srs_failed_count: 0,
                add_ons_failed_count: updateAddOnsFailedCount,
              });
              return;
            }

            toasterStore.push(
              alert(
                t('order.order_updated', { name: updatedOrder.orderId }),
                AlertTypes.success,
              ),
            );

            onOrderSaved?.(updatedOrder, 'updated');
            setPartialFailureOrder(null);
            setLastSubmitSummary({
              orderStatus: 'saved',
              ratesAttempted: srsResult !== null,
              ratesSucceededCount: srsSummary.succeededCount,
              ratesFailedCount: 0,
              addOnsFailedCount: 0,
            });
          } else {
            // Copy & Edit: defer the starred id off the POST (the form value
            // is a clientKey or a project-owned server id; a clientKey would
            // trip the BE's ownership validation) and PATCH the resolved id
            // after the SRS save — same flow as the legacy branch above.
            const sourceDefaultSrsId = newFormData.defaultSiteRateScheduleId;
            const initialFormData =
              isCopyAndEdit && sourceDefaultSrsId
                ? { ...newFormData, defaultSiteRateScheduleId: null }
                : newFormData;
            const createdOrder = await createOrderV3({
              body: deparseCreateV3(initialFormData, distanceUnit),
            });
            const srsCommit = await commitPendingSrsOps(createdOrder);
            if (srsCommit.status === 'aborted') {
              reportSrsCommitAborted(createdOrder, srsCommit.rateOpCount);

              return;
            }
            const srsResult = srsCommit.result;
            const srsSummary = handleSrsSaveResult(srsResult);
            if (srsSummary.hasFailures) {
              // Order landed but at least one SRS failed — remember the
              // server-side order so the retry submit PATCHes it instead
              // of POSTing a duplicate.
              setPartialFailureOrder(createdOrder);
              setLastSubmitSummary({
                orderStatus: 'saved',
                ratesAttempted: true,
                ratesSucceededCount: srsSummary.succeededCount,
                ratesFailedCount: srsSummary.failedCount,
                addOnsFailedCount: 0,
              });
              AnalyticsController.track('order_form_v3_submit_partial', {
                is_editing: isEditing,
                srs_failed_count: srsSummary.failedCount,
                add_ons_failed_count: 0,
              });
              return;
            }

            if ((isCopyAndEdit || starNeedsRepointAfterDecouple) && sourceDefaultSrsId) {
              const resolvedDefaultSrsId = starredSiteRateScheduleIdAfterSave(
                resolveDefaultSiteRateScheduleId(sourceDefaultSrsId, srsResult),
              );
              try {
                await updateOrderV3({
                  id: createdOrder.id,
                  body: deparseUpdateV3(
                    {
                      ...newFormData,
                      defaultSiteRateScheduleId: resolvedDefaultSrsId,
                    },
                    { defaultSiteRateScheduleId: true },
                    distanceUnit,
                  ),
                });
              } catch {
                // updateOrderV3 already toasts non-422 failures. Fall through
                // to the existing success path so the user sees the order was
                // created; the starred reference is the only thing missing
                // and the user can re-star manually.
              }
            }

            const {
              vendorAddOnAssignments,
              customerAddOnAssignments,
              driverAddOnAssignments,
            } = formData;

            const createBatches = [
              { items: vendorAddOnAssignments ?? [], accountType: AccountType.VENDOR },
              {
                items: customerAddOnAssignments ?? [],
                accountType: AccountType.CUSTOMER,
              },
              { items: driverAddOnAssignments ?? [], accountType: undefined },
            ];
            const createAddOnResults = await Promise.allSettled(
              createBatches.map((b) =>
                b.items.length > 0
                  ? processAddOnAssignmentsForOrder({
                      companyId,
                      order: createdOrder,
                      addOnAssignments: b.items,
                      accountType: b.accountType,
                    })
                  : Promise.resolve([]),
              ),
            );
            const createAddOnsFailedCount = createAddOnResults.reduce(
              (sum, r, i) =>
                r.status === 'rejected' ? sum + createBatches[i].items.length : sum,
              0,
            );
            if (createAddOnsFailedCount > 0) {
              setPartialFailureOrder(createdOrder);
              setLastSubmitSummary({
                orderStatus: 'saved',
                ratesAttempted: srsResult !== null,
                ratesSucceededCount: srsSummary.succeededCount,
                ratesFailedCount: 0,
                addOnsFailedCount: createAddOnsFailedCount,
                addOnsDuplicate: hasDuplicateAddOnAssignmentFailure(createAddOnResults),
              });
              AnalyticsController.track('order_form_v3_submit_partial', {
                is_editing: isEditing,
                srs_failed_count: 0,
                add_ons_failed_count: createAddOnsFailedCount,
              });
              return;
            }

            if (createdOrder?.id) {
              await onOrderCreated(createdOrder.id, createdOrder.company?.id);
              if (disableDispatchDeepLink) {
                toasterStore.push(
                  alert(
                    t('dispatch.order.created', { name: createdOrder.orderId }),
                    AlertTypes.success,
                  ),
                );
              } else if (shouldAutoOpenCreatedOrder) {
                ordersDispatchStore.setDeepLinkedOrder(createdOrder, {
                  source: 'created',
                });
                toasterStore.push(
                  alert(
                    t('dispatch.order.created', { name: createdOrder.orderId }),
                    AlertTypes.success,
                  ),
                );
              } else {
                showOrderCreatedToaster(createdOrder);
              }
              onOrderSaved?.(createdOrder, 'created');
            }
            setPartialFailureOrder(null);
            setLastSubmitSummary({
              orderStatus: 'saved',
              ratesAttempted: srsResult !== null,
              ratesSucceededCount: srsSummary.succeededCount,
              ratesFailedCount: 0,
              addOnsFailedCount: 0,
            });
          }

          AnalyticsController.track('order_form_v3_submit_succeeded', {
            is_editing: isEditing,
            section_ms: formRef.current?.getSectionMs(),
          });
          handleClose({} as React.MouseEvent<HTMLButtonElement>);
        } catch (error) {
          // Parent create/update rejected. A 422 carries ModelError_Item[] —
          // surface each item inline via the Provider's mapping. Optional-
          // chain the FULL path: a non-422 failure (network error, 500 with a
          // non-JSON body) has no `data.error.errors`, and a TypeError here
          // would skip the footer update (mirrors ProjectDialog's standard).
          // Non-422 failures already toasted inside the hooks.
          const modelError = error as AxiosError<ModelError_Response>;
          const errorItems = modelError.response?.data?.error?.errors || [];
          setAsyncErrors(errorItems);
          AnalyticsController.track('order_form_v3_submit_failed', {
            is_editing: isEditing,
            error_status: modelError.response?.status,
            error_count: errorItems.length,
          });
          setLastSubmitSummary({
            orderStatus: 'failed',
            ratesAttempted: false,
            ratesSucceededCount: 0,
            ratesFailedCount: 0,
            addOnsFailedCount: 0,
          });
        }
      },
      [
        disableDispatchDeepLink,
        isEditing,
        isCopyAndEdit,
        order,
        onOrderCreated,
        handleClose,
        onOrderSaved,
        ordersDispatchStore,
        shouldAutoOpenCreatedOrder,
        showOrderCreatedToaster,
        processSiteRateScheduleOperationsResilient,
        partialFailureOrder,
        toasterStore,
      ],
    );

    const handleOrderFormSubmitV3 = useCallback(() => {
      void formRef.current?.submit((payload) => {
        void saveOrderV3(payload);
      });
    }, [saveOrderV3]);

    useEffect(() => {
      if (!companyId) {
        return;
      }

      if (isOpen) {
        if (services?.length) {
          setIsLoading((state) => ({ ...state, services: false }));
        } else {
          getAllServices(companyId).then(() => {
            setIsLoading((state) => ({ ...state, services: false }));
          });
        }
      }
    }, [companyId, services?.length, isOpen]);

    return (
      <Drawer
        anchor="right"
        keepMounted={keepMounted}
        onClose={handleClose}
        onTransitionExited={handleTransitionExited}
        open={drawerState.isOpen}
        sx={{
          [`& .${drawerClasses.paper}`]: {
            height: 'calc(100dvh - var(--tread-agent-dock-height, 0px))',
            maxWidth: '1200px',
            overflow: 'hidden',
            width: '80%',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              alignItems: 'center',
              borderBottom: `solid 1px ${theme.palette.divider}`,
              display: 'flex',
              flex: 0,
              gap: 1,
              justifyContent: 'space-between',
              px: 2,
              py: 1,
            }}
          >
            <Typography variant="h5" sx={{ flex: 1 }}>
              {isCopyAndEdit
                ? t('order.copy_and_edit')
                : order
                  ? t('order.update_order')
                  : t('order.create_order')}
              {order && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: '16px',
                    color: theme.brandV2.colors.treadGray3,
                    ml: 2,
                  }}
                >
                  {order.orderId}
                </Typography>
              )}
            </Typography>

            {order && !isCopyAndEdit && (
              <PricingHistoryButton
                analyticsSurface="order_form"
                orderId={order.id}
                subtitle={t('loads.pricing.history.order_ref', { ref: order.orderId })}
              />
            )}

            <IconButton
              color="inherit"
              size="small"
              onClick={handleClose}
              data-ui={`page-dispatch__order__order-${order?.orderId}__button`}
              data-test-id="page-dispatch__order-form__close-button"
            >
              <Close sx={{ width: 16, height: 16 }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              bgcolor: theme.semantic.surfaceSunken,
              flex: 1,
              px: 2,
              pb: 1,
              overflow: 'auto',
            }}
          >
            {isLoadingInitialData ? (
              <LoadingSpinner isVisible={true} sx={{ height: '100%' }} />
            ) : (
              // The Provider owns useForm and renders FormProvider itself
              // (a core plan invariant).
              <OrderFormV3Provider
                defaultDate={defaultDate}
                defaultOrder={order ?? null}
                errors={asyncErrors}
                isCopyAndEdit={isCopyAndEdit}
                onFormStateChange={handleFormStateChange}
                onTempDefaultSiteRemoved={handleTempDefaultSiteRemoved}
              >
                <OrderFormV3 ref={formRef} />
              </OrderFormV3Provider>
            )}
          </Box>

          <Box
            sx={{
              alignItems: 'center',
              borderTop: `solid 1px ${theme.palette.divider}`,
              display: 'flex',
              flex: 0,
              gap: 1,
              justifyContent: 'flex-end',
              p: 1,
            }}
          >
            {/*
              Footer status row mirroring ProjectDialog's mock — after a submit attempt,
              summarize order + freight-rate save outcomes next to the CTAs instead of as
              a transient toast.
            */}
            {lastSubmitSummary && (
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: 2,
                  pr: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={(th) => ({
                    fontWeight: 600,
                    color:
                      lastSubmitSummary.orderStatus === 'saved'
                        ? th.palette.success.main
                        : th.palette.brandV2Red.main,
                  })}
                >
                  {lastSubmitSummary.orderStatus === 'saved'
                    ? t('project.form.order_saved')
                    : t('project.form.order_failed_to_save')}
                </Typography>
                {lastSubmitSummary.ratesAttempted && (
                  <Typography
                    variant="body2"
                    sx={(th) => ({
                      fontWeight: 600,
                      color:
                        lastSubmitSummary.ratesFailedCount > 0
                          ? th.palette.brandV2Red.main
                          : th.palette.success.main,
                    })}
                  >
                    {lastSubmitSummary.ratesFailedCount > 0
                      ? t('project.form.rates_failed_to_save', {
                          count: lastSubmitSummary.ratesFailedCount,
                        })
                      : t('project.form.rates_saved', {
                          count: lastSubmitSummary.ratesSucceededCount,
                        })}
                  </Typography>
                )}
                {lastSubmitSummary.addOnsFailedCount > 0 && (
                  <Typography
                    variant="body2"
                    sx={(th) => ({
                      fontWeight: 600,
                      color: th.palette.brandV2Red.main,
                    })}
                  >
                    {lastSubmitSummary.addOnsDuplicate
                      ? t('project.form.rate_addon_duplicate_failed')
                      : t('project.form.rate_addon_save_failed', {
                          count: lastSubmitSummary.addOnsFailedCount,
                        })}
                  </Typography>
                )}
              </Box>
            )}

            <SmallButton
              color="brandV2OrangeDark"
              onClick={onMinimize}
              endIcon={<ExpandCircleDownOutlined />}
              variant="text"
              sx={{
                '&.MuiButtonBase-root': {
                  height: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
                  mr: -0.5,
                  pr: 0.5,
                  pl: 1,
                  py: 0,
                  '&:hover': { borderColor: theme.brandV2.colors.treadOrange },
                  '.MuiButton-endIcon': { ml: 0.5, mr: 0 },
                  '.MuiSvgIcon-root': {
                    fontSize: '24px',
                    transform: 'rotate(-90deg)',
                  },
                },
              }}
            >
              {t('dispatch.dispatch_v2.minimize_order_form')}
            </SmallButton>

            <SmallButton
              color="brandV2Yellow"
              disabled={ordersDispatchStore.isSavingOrder || !formIsDirty}
              onClick={handleOrderFormSubmitV3}
              type="button"
              sx={{ position: 'relative' }}
            >
              <LoadingSpinner
                isVisible={ordersDispatchStore.isSavingOrder}
                loadingIndicatorColor="inherit"
                loadingIndicatorSize={13}
                sx={{ position: 'absolute', inset: 0 }}
              />

              <Typography
                component="span"
                sx={{
                  color: 'inherit',
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                  visibility: ordersDispatchStore.isSavingOrder ? 'hidden' : 'visible',
                }}
              >
                {t(`actions.${order ? 'submit' : 'create'}`)}
              </Typography>
            </SmallButton>
          </Box>
        </Box>
      </Drawer>
    );
  },
);

export default OrderForm;
